import { ChatGPTModelType } from "@aix/apiutils/src/llms/enums"
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  cn,
  Label,
  ScrollArea,
  Separator,
  Slider,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from "@aix/ui"
import { filter, map, size } from "lodash-es"
import { BookmarkPlus, Check, Clock, Copy, History, Play, RefreshCw, Trash2, X } from "lucide-react"
import { useCallback, useEffect, useState } from "react"
import toast from "react-hot-toast"
import { trpc } from "../../lib/trpc"

// Keep this list aligned with the models currently enabled by the gateway.
// Add a model only after its gateway pricing and provider adapter are enabled.
const AVAILABLE_MODELS = [{ id: ChatGPTModelType.GPT_4o_mini, name: "GPT-4o mini", provider: "OpenAI" }]

const MAX_MODELS = 5

// Storage keys
const STORAGE_KEYS = {
  SYSTEM_PROMPT: "llm-playground-system-prompt",
  LAST_MODELS: "llm-playground-last-models",
  LAST_TEMPERATURE: "llm-playground-last-temperature",
  LAST_MAX_TOKENS: "llm-playground-last-max-tokens",
  RESULTS_HISTORY: "llm-playground-results-history",
  OUTPUT_MODE: "llm-playground-output-mode",
}

interface TestResult {
  id: string
  // Shared across every result from the same `handleRunTest` invocation so
  // when these rows are bookmarked into `llm_bench_runs` they bucket together
  // in the Benchmarks compare view.
  runGroupKey: string
  model: string
  modelName: string
  success: boolean
  response: string | null
  error: string | null
  durationMs: number
  tokenUsage?: {
    inputTokens?: number
    outputTokens?: number
    totalTokens?: number
    totalCost?: number
  }
}

interface HistoryEntry {
  id: string
  systemPrompt: string
  userInput: string
  results: TestResult[]
  createdAt: string
  outputMode: "text" | "json"
}

export default function LLMPlaygroundScreen({ gatewayInspection = false }: { gatewayInspection?: boolean } = {}) {
  // State
  const [systemPrompt, setSystemPrompt] = useState("")
  const [userInput, setUserInput] = useState("")
  const [selectedModels, setSelectedModels] = useState<string[]>([ChatGPTModelType.GPT_4o_mini])
  const [temperature, setTemperature] = useState(0.7)
  const [maxTokens, setMaxTokens] = useState(gatewayInspection ? 100 : 2000)
  const [outputMode, setOutputMode] = useState<"text" | "json">("text")
  const [isRunning, setIsRunning] = useState(false)
  const [results, setResults] = useState<TestResult[]>([])
  const [history, setHistory] = useState<HistoryEntry[]>([])
  const [activeTab, setActiveTab] = useState<"benchmarks" | "run" | "history">(() => {
    if (gatewayInspection) return "run"
    const stored = typeof window !== "undefined" ? localStorage.getItem("llm-playground-tab") : null
    return stored === "run" || stored === "history" ? stored : "benchmarks"
  })
  const [benchmarkFilter, setBenchmarkFilter] = useState<{ model?: string; group?: string }>({})
  const [expandedRow, setExpandedRow] = useState<string | null>(null)

  useEffect(() => {
    if (gatewayInspection) return
    localStorage.setItem("llm-playground-tab", activeTab)
  }, [activeTab, gatewayInspection])

  const benchmarksQuery = trpc.ai.listBenchmarks.useQuery(
    { limit: 200, modelId: benchmarkFilter.model, groupKey: benchmarkFilter.group },
    { enabled: !gatewayInspection && activeTab === "benchmarks" },
  )
  const trpcUtils = trpc.useUtils()
  const deleteBenchmarkMutation = trpc.ai.deleteBenchmark.useMutation({
    onSuccess: () => {
      toast.success("Deleted")
      void trpcUtils.ai.listBenchmarks.invalidate()
    },
    onError: (err) => toast.error(err.message || "Failed to delete"),
  })

  // Load from localStorage on mount
  useEffect(() => {
    if (gatewayInspection) return
    const savedPrompt = localStorage.getItem(STORAGE_KEYS.SYSTEM_PROMPT)
    if (savedPrompt) setSystemPrompt(savedPrompt)

    const savedModels = localStorage.getItem(STORAGE_KEYS.LAST_MODELS)
    if (savedModels) {
      try {
        const parsed = JSON.parse(savedModels)
        if (Array.isArray(parsed) && parsed.length > 0) {
          const availableModelIds = new Set<string>(AVAILABLE_MODELS.map((model) => model.id))
          const supportedModels = parsed.filter(
            (model): model is string => typeof model === "string" && availableModelIds.has(model),
          )
          if (supportedModels.length > 0) {
            setSelectedModels(supportedModels)
          }
        }
      } catch {
        // ignore
      }
    }

    const savedTemp = localStorage.getItem(STORAGE_KEYS.LAST_TEMPERATURE)
    if (savedTemp) setTemperature(parseFloat(savedTemp))

    const savedTokens = localStorage.getItem(STORAGE_KEYS.LAST_MAX_TOKENS)
    if (savedTokens) setMaxTokens(parseInt(savedTokens))

    const savedMode = localStorage.getItem(STORAGE_KEYS.OUTPUT_MODE)
    if (savedMode === "json" || savedMode === "text") setOutputMode(savedMode)

    const savedHistory = localStorage.getItem(STORAGE_KEYS.RESULTS_HISTORY)
    if (savedHistory) {
      try {
        setHistory(JSON.parse(savedHistory))
      } catch {
        // ignore
      }
    }
  }, [gatewayInspection])

  // Save to localStorage
  useEffect(() => {
    if (gatewayInspection) return
    localStorage.setItem(STORAGE_KEYS.SYSTEM_PROMPT, systemPrompt)
  }, [systemPrompt, gatewayInspection])

  useEffect(() => {
    if (gatewayInspection) return
    localStorage.setItem(STORAGE_KEYS.LAST_MODELS, JSON.stringify(selectedModels))
  }, [selectedModels, gatewayInspection])

  useEffect(() => {
    if (gatewayInspection) return
    localStorage.setItem(STORAGE_KEYS.LAST_TEMPERATURE, temperature.toString())
  }, [temperature, gatewayInspection])

  useEffect(() => {
    if (gatewayInspection) return
    localStorage.setItem(STORAGE_KEYS.LAST_MAX_TOKENS, maxTokens.toString())
  }, [maxTokens, gatewayInspection])

  useEffect(() => {
    if (gatewayInspection) return
    localStorage.setItem(STORAGE_KEYS.OUTPUT_MODE, outputMode)
  }, [outputMode, gatewayInspection])

  // tRPC mutation for testing prompts
  const testMutation = trpc.ai.testPrompt.useMutation()
  const saveBenchmarkMutation = trpc.ai.saveBenchmark.useMutation({
    onSuccess: () => toast.success("Saved to benchmarks"),
    onError: (err) => toast.error(err.message || "Failed to save benchmark"),
  })

  const handleSaveBenchmark = useCallback(
    (result: TestResult) => {
      if (gatewayInspection) return
      const tagFromMode = outputMode === "json" ? "json-mode" : "text-mode"
      saveBenchmarkMutation.mutate({
        systemPrompt,
        userInput,
        model: result.model as any,
        temperature,
        maxTokens,
        mode: outputMode,
        success: result.success,
        responseText: result.response,
        errorMessage: result.error,
        inputTokens: result.tokenUsage?.inputTokens ?? null,
        outputTokens: result.tokenUsage?.outputTokens ?? null,
        totalTokens: result.tokenUsage?.totalTokens ?? null,
        totalCostUsd: result.tokenUsage?.totalCost ?? null,
        latencyMs: result.durationMs,
        tags: ["playground", tagFromMode],
        // Shared across every model from the same Run, so bookmarking 2+
        // results from one run buckets them as a compare card.
        groupKey: result.runGroupKey,
      })
    },
    [systemPrompt, userInput, temperature, maxTokens, outputMode, saveBenchmarkMutation, gatewayInspection],
  )

  // Toggle model selection
  const handleToggleModel = useCallback((modelId: string) => {
    setSelectedModels((prev) => {
      if (prev.includes(modelId)) {
        if (prev.length === 1) {
          toast.error("Select at least one model")
          return prev
        }
        return prev.filter((m) => m !== modelId)
      } else {
        if (prev.length >= MAX_MODELS) {
          toast.error(`Maximum ${MAX_MODELS} models allowed`)
          return prev
        }
        return [...prev, modelId]
      }
    })
  }, [])

  // Run tests
  const handleRunTest = useCallback(async () => {
    if (!systemPrompt.trim()) {
      toast.error("Please enter a system prompt")
      return
    }
    if (!userInput.trim()) {
      toast.error("Please enter user input")
      return
    }
    if (selectedModels.length === 0) {
      toast.error("Please select at least one model")
      return
    }

    setIsRunning(true)
    setResults([])

    const startTime = Date.now()
    const runGroupKey = `run-${new Date(startTime).toISOString().replace(/[:.]/g, "-")}`
    const testResults: TestResult[] = []

    try {
      // Run all models in parallel
      const promises = selectedModels.map(async (modelId) => {
        const modelInfo = AVAILABLE_MODELS.find((m) => m.id === modelId)
        const modelStart = Date.now()

        try {
          const result = await testMutation.mutateAsync({
            operationId: crypto.randomUUID(),
            systemPrompt,
            userInput,
            model: modelId as any,
            temperature,
            maxTokens,
            mode: outputMode,
          })

          return {
            id: `${modelId}-${Date.now()}`,
            runGroupKey,
            model: modelId,
            modelName: modelInfo?.name || modelId,
            success: true,
            response: result.response,
            error: null,
            durationMs: Date.now() - modelStart,
            tokenUsage: result.usage,
          }
        } catch (err) {
          return {
            id: `${modelId}-${Date.now()}`,
            runGroupKey,
            model: modelId,
            modelName: modelInfo?.name || modelId,
            success: false,
            response: null,
            error: (err as Error).message,
            durationMs: Date.now() - modelStart,
          }
        }
      })

      const results = await Promise.all(promises)
      testResults.push(...results)
      setResults(results)

      // Save to history
      const historyEntry: HistoryEntry = {
        id: Date.now().toString(),
        systemPrompt,
        userInput,
        results,
        createdAt: new Date().toISOString(),
        outputMode,
      }

      setHistory((prev) => {
        const updated = [historyEntry, ...prev].slice(0, 50) // Keep last 50
        if (!gatewayInspection) localStorage.setItem(STORAGE_KEYS.RESULTS_HISTORY, JSON.stringify(updated))
        return updated
      })

      const successCount = filter(results, (r) => r.success).length
      toast.success(`${successCount}/${results.length} tests completed`)
    } catch (error) {
      toast.error(`Error: ${(error as Error).message}`)
    } finally {
      setIsRunning(false)
    }
  }, [systemPrompt, userInput, selectedModels, temperature, maxTokens, outputMode, testMutation, gatewayInspection])

  // Load from history
  const handleLoadHistory = useCallback((entry: HistoryEntry) => {
    setSystemPrompt(entry.systemPrompt)
    setUserInput(entry.userInput)
    setResults(entry.results)
    setOutputMode(entry.outputMode)
    setActiveTab("run")
    toast.success("Loaded from history")
  }, [])

  // Delete history entry
  const handleDeleteHistory = useCallback(
    (id: string) => {
      setHistory((prev) => {
        const updated = prev.filter((h) => h.id !== id)
        if (!gatewayInspection) localStorage.setItem(STORAGE_KEYS.RESULTS_HISTORY, JSON.stringify(updated))
        return updated
      })
      toast.success("Deleted")
    },
    [gatewayInspection],
  )

  // Clear all history
  const handleClearHistory = useCallback(() => {
    setHistory([])
    if (!gatewayInspection) localStorage.removeItem(STORAGE_KEYS.RESULTS_HISTORY)
    toast.success("History cleared")
  }, [gatewayInspection])

  // Copy result
  const handleCopyResult = useCallback((text: string) => {
    void navigator.clipboard.writeText(text)
    toast.success("Copied to clipboard")
  }, [])

  // Copy all context for external use
  const handleCopyAllContext = useCallback(() => {
    const sections: string[] = []
    sections.push(`## System Prompt\n\n\`\`\`\n${systemPrompt}\n\`\`\``)
    sections.push(`## User Input\n\n${userInput}`)

    if (results.length > 0) {
      sections.push(`## Results (${results.length} model${results.length > 1 ? "s" : ""})`)
      results.forEach((result) => {
        sections.push(`\n### ${result.modelName}`)
        sections.push(`- **Status:** ${result.success ? "Success" : "Failed"}`)
        sections.push(`- **Duration:** ${result.durationMs}ms`)
        if (result.error) {
          sections.push(`- **Error:** ${result.error}`)
        }
        if (result.response) {
          sections.push(`\n**Response:**\n\`\`\`${outputMode}\n${result.response}\n\`\`\``)
        }
      })
    }

    void navigator.clipboard.writeText(sections.join("\n\n"))
    toast.success("Copied all context")
  }, [systemPrompt, userInput, results, outputMode])

  return (
    <div className="w-full p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">LLM Playground</h1>
          <p className="text-muted-foreground">Test and compare prompts across multiple models</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleCopyAllContext}
          disabled={!systemPrompt && results.length === 0}
        >
          <Copy size={16} className="mr-2" />
          Copy All
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "benchmarks" | "run" | "history")}>
        <TabsList className="mb-4">
          {!gatewayInspection && (
            <TabsTrigger value="benchmarks">
              <BookmarkPlus size={14} className="mr-1.5" />
              Benchmarks
            </TabsTrigger>
          )}
          <TabsTrigger value="run">
            <Play size={14} className="mr-1.5" />
            Run
          </TabsTrigger>
          <TabsTrigger value="history">
            <History size={14} className="mr-1.5" />
            History ({size(history)})
          </TabsTrigger>
        </TabsList>

        {!gatewayInspection && (
          <TabsContent value="benchmarks">
            {/* Compact + grouped saved benchmarks. Rows that share a `groupKey`
              get rendered as a side-by-side compare card. */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <CardTitle className="text-base">Saved Benchmarks</CardTitle>
                    <CardDescription className="text-xs">
                      Persisted in <code>llm_bench_runs</code>. Add rows via the bookmark icon below, or via the agentic{" "}
                      <code>/api-llm-bench</code> CLI.
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="filter by group key…"
                      value={benchmarkFilter.group ?? ""}
                      onChange={(e) => setBenchmarkFilter((f) => ({ ...f, group: e.target.value || undefined }))}
                      className="h-7 rounded border border-border bg-background px-2 text-xs"
                    />
                    <input
                      type="text"
                      placeholder="filter by model…"
                      value={benchmarkFilter.model ?? ""}
                      onChange={(e) => setBenchmarkFilter((f) => ({ ...f, model: e.target.value || undefined }))}
                      className="h-7 rounded border border-border bg-background px-2 text-xs"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {benchmarksQuery.isLoading ? (
                  <div className="py-8 text-center text-sm text-muted-foreground">Loading benchmarks…</div>
                ) : benchmarksQuery.error ? (
                  <div className="py-8 text-center text-sm text-destructive">
                    Failed to load benchmarks: {benchmarksQuery.error.message}
                  </div>
                ) : size(benchmarksQuery.data) === 0 ? (
                  <div className="py-8 text-center text-sm text-muted-foreground">
                    No saved benchmarks yet. Run a test in the <strong>Run</strong> tab and click the bookmark icon, or
                    use the <code>/api-llm-bench</code> CLI.
                  </div>
                ) : (
                  <ScrollArea className="h-[calc(100vh-260px)]">
                    <div className="space-y-4 pr-4">
                      {(() => {
                        const rows = benchmarksQuery.data ?? []
                        // Group rows: those with a `groupKey` get bucketed together
                        // (compare view); those without are listed individually.
                        const groups = new Map<string, typeof rows>()
                        const standalone: typeof rows = []
                        for (const r of rows) {
                          if (r.groupKey) {
                            const arr = groups.get(r.groupKey) ?? []
                            arr.push(r)
                            groups.set(r.groupKey, arr)
                          } else {
                            standalone.push(r)
                          }
                        }
                        return (
                          <>
                            {Array.from(groups.entries()).map(([groupKey, groupRows]) => (
                              <div key={groupKey} className="rounded-lg border border-border bg-secondary/20 p-2">
                                <div className="mb-2 flex items-center justify-between px-1">
                                  <div className="flex items-center gap-2 text-xs">
                                    <Badge variant="outline" className="text-[10px]">
                                      group: {groupKey}
                                    </Badge>
                                    <span className="text-muted-foreground">{groupRows.length} model(s)</span>
                                  </div>
                                  <span className="text-[10px] text-muted-foreground">
                                    {new Date(groupRows[0].createdAt).toLocaleString()}
                                  </span>
                                </div>
                                <div className="mb-2 px-1 text-xs text-foreground line-clamp-2">
                                  <span className="text-muted-foreground">prompt:</span> {groupRows[0].userPrompt}
                                </div>
                                {/* Side-by-side compact comparison */}
                                <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                  {groupRows.map((row) => (
                                    <BenchmarkCard
                                      key={row.id}
                                      row={row}
                                      expanded={expandedRow === row.id}
                                      onToggle={() => setExpandedRow(expandedRow === row.id ? null : row.id)}
                                      onDelete={() => deleteBenchmarkMutation.mutate({ id: row.id })}
                                      deleteDisabled={deleteBenchmarkMutation.isLoading}
                                    />
                                  ))}
                                </div>
                              </div>
                            ))}
                            {standalone.length > 0 && (
                              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                {standalone.map((row) => (
                                  <BenchmarkCard
                                    key={row.id}
                                    row={row}
                                    expanded={expandedRow === row.id}
                                    onToggle={() => setExpandedRow(expandedRow === row.id ? null : row.id)}
                                    onDelete={() => deleteBenchmarkMutation.mutate({ id: row.id })}
                                    deleteDisabled={deleteBenchmarkMutation.isLoading}
                                  />
                                ))}
                              </div>
                            )}
                          </>
                        )
                      })()}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="history">
          {/* History View */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Test History</CardTitle>
                <Button variant="ghost" size="sm" onClick={handleClearHistory}>
                  <Trash2 size={14} className="mr-1" />
                  Clear All
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {size(history) === 0 ? (
                <div className="py-8 text-center text-muted-foreground">No history yet</div>
              ) : (
                <ScrollArea className="h-[calc(100vh-300px)]">
                  <div className="space-y-2 pr-4">
                    {map(history, (entry) => (
                      <button
                        key={entry.id}
                        onClick={() => handleLoadHistory(entry)}
                        className="w-full rounded-lg border border-border p-3 text-left transition-colors hover:bg-secondary/50"
                      >
                        <div className="mb-2 flex items-center justify-between">
                          <div className="flex flex-wrap gap-1">
                            {map(entry.results, (r) => (
                              <Badge key={r.id} variant={r.success ? "default" : "destructive"} className="text-[10px]">
                                {r.modelName}
                              </Badge>
                            ))}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDeleteHistory(entry.id)
                            }}
                          >
                            <Trash2 size={12} />
                          </Button>
                        </div>
                        <div className="mb-1 line-clamp-2 text-sm text-foreground">{entry.userInput}</div>
                        <div className="text-[10px] text-muted-foreground">
                          {new Date(entry.createdAt).toLocaleString()}
                        </div>
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="run">
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            {/* Left Column - Settings */}
            <div className="space-y-4">
              {/* Model Selector */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">
                    Models ({size(selectedModels)}/{MAX_MODELS})
                  </CardTitle>
                  <CardDescription>Select up to {MAX_MODELS} models to compare</CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[200px]">
                    <div className="space-y-1 pr-4">
                      {map(AVAILABLE_MODELS, (model) => (
                        <label
                          key={model.id}
                          className={cn(
                            "flex items-center gap-2 rounded-md px-2 py-1.5 cursor-pointer transition-colors select-none",
                            selectedModels.includes(model.id) ? "bg-primary/10" : "hover:bg-secondary/50",
                          )}
                        >
                          <Checkbox
                            checked={selectedModels.includes(model.id)}
                            onCheckedChange={() => handleToggleModel(model.id)}
                          />
                          <span className="flex-1 text-sm text-foreground">{model.name}</span>
                          <span className="text-[10px] text-muted-foreground">{model.provider}</span>
                        </label>
                      ))}
                    </div>
                  </ScrollArea>
                  {size(selectedModels) > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {map(selectedModels, (modelId) => {
                        const model = AVAILABLE_MODELS.find((m) => m.id === modelId)
                        return (
                          <Badge key={modelId} variant="secondary" className="text-xs">
                            {model?.name || modelId}
                            <button onClick={() => handleToggleModel(modelId)} className="ml-1 hover:text-destructive">
                              <X size={10} />
                            </button>
                          </Badge>
                        )
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Settings */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Temperature: {temperature}</Label>
                    <Slider
                      value={[temperature]}
                      onValueChange={(v) => setTemperature(v[0] ?? 0.7)}
                      min={0}
                      max={2}
                      step={0.1}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Tokens: {maxTokens}</Label>
                    <Slider
                      value={[maxTokens]}
                      onValueChange={(v) => setMaxTokens(v[0] ?? 2000)}
                      min={100}
                      max={8000}
                      step={100}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Output Mode</Label>
                    <Tabs value={outputMode} onValueChange={(v) => setOutputMode(v as "text" | "json")}>
                      <TabsList className="w-full">
                        <TabsTrigger value="text" className="flex-1">
                          Text
                        </TabsTrigger>
                        <TabsTrigger value="json" className="flex-1">
                          JSON
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                  </div>
                </CardContent>
              </Card>

              {/* Run Button */}
              <Button
                onClick={handleRunTest}
                disabled={isRunning || !systemPrompt.trim() || !userInput.trim()}
                className="w-full"
              >
                {isRunning ? (
                  <>Running {size(selectedModels)} tests...</>
                ) : (
                  <>
                    <Play size={16} className="mr-2" />
                    Run Test ({size(selectedModels)} model{size(selectedModels) > 1 ? "s" : ""})
                  </>
                )}
              </Button>
            </div>

            {/* Middle Column - Prompts */}
            <div className="space-y-4">
              <Card className="h-full">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Prompts</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>System Prompt</Label>
                    <Textarea
                      value={systemPrompt}
                      onChange={(e) => setSystemPrompt(e.target.value)}
                      rows={10}
                      className="font-mono text-xs"
                      placeholder="Enter system prompt..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>User Input</Label>
                    <Textarea
                      value={userInput}
                      onChange={(e) => setUserInput(e.target.value)}
                      rows={8}
                      placeholder="Enter user message to test..."
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Results */}
            <div className="space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Results</CardTitle>
                </CardHeader>
                <CardContent>
                  {size(results) === 0 && !isRunning && (
                    <div className="flex h-[200px] items-center justify-center text-muted-foreground">
                      Run a test to see results
                    </div>
                  )}

                  {isRunning && (
                    <div className="flex h-[200px] items-center justify-center">
                      <div className="text-center">
                        <div className="mb-2 animate-pulse text-lg text-foreground">Processing...</div>
                        <div className="text-sm text-muted-foreground">
                          Running {size(selectedModels)} model{size(selectedModels) > 1 ? "s" : ""} in parallel
                        </div>
                      </div>
                    </div>
                  )}

                  {size(results) > 0 && !isRunning && (
                    <ScrollArea className="h-[calc(100vh-300px)]">
                      <div className="space-y-6 pr-4">
                        {map(results, (result, idx) => (
                          <div key={result.id} className="space-y-3">
                            {/* Model Header */}
                            <div className="flex items-center justify-between">
                              <div className="flex flex-wrap gap-2">
                                <Badge variant={result.success ? "default" : "destructive"}>
                                  {result.success ? (
                                    <Check size={12} className="mr-1" />
                                  ) : (
                                    <X size={12} className="mr-1" />
                                  )}
                                  {result.modelName}
                                </Badge>
                                <Badge variant="outline">
                                  <Clock size={10} className="mr-1" />
                                  {result.durationMs}ms
                                </Badge>
                              </div>
                              <div className="flex items-center gap-1">
                                {!gatewayInspection && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleSaveBenchmark(result)}
                                    disabled={saveBenchmarkMutation.isLoading}
                                    title="Save to benchmarks"
                                  >
                                    <BookmarkPlus size={12} />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => result.response && handleCopyResult(result.response)}
                                  disabled={!result.response}
                                >
                                  <Copy size={12} />
                                </Button>
                              </div>
                            </div>

                            {result.error && (
                              <div className="rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
                                {result.error}
                              </div>
                            )}

                            {result.response && (
                              <div className="rounded-md bg-secondary p-3">
                                <pre className="whitespace-pre-wrap text-xs text-foreground font-mono overflow-auto max-h-[400px]">
                                  {result.response}
                                </pre>
                              </div>
                            )}

                            {result.tokenUsage && (
                              <div className="rounded-md bg-secondary/50 p-2 text-[10px] font-mono text-muted-foreground">
                                Tokens: {result.tokenUsage.totalTokens || "N/A"} | Cost: $
                                {result.tokenUsage.totalCost?.toFixed(4) || "N/A"}
                              </div>
                            )}

                            {idx < results.length - 1 && <Separator />}
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Compact card for a single benchmark row. Click to expand/collapse the full
// prompt + response inline so comparing N models across the same prompt is
// glanceable without paginating modals.
// ─────────────────────────────────────────────────────────────────────────────

interface BenchmarkRow {
  id: string
  createdAt: string | Date
  modelId: string
  provider: string
  success: boolean
  responseText: string | null
  errorMessage: string | null
  latencyMs: number
  inputTokens: number | null
  outputTokens: number | null
  totalTokens: number | null
  totalCostUsd: number | null
  mode: string
  systemPrompt: string
  userPrompt: string
  tags: string[] | null
  groupKey: string | null
}

function BenchmarkCard({
  row,
  expanded,
  onToggle,
  onDelete,
  deleteDisabled,
}: {
  row: BenchmarkRow
  expanded: boolean
  onToggle: () => void
  onDelete: () => void
  deleteDisabled: boolean
}) {
  return (
    <div className="rounded border border-border bg-background p-2 text-xs transition-colors hover:border-primary/40">
      <div className="mb-1 flex items-start justify-between gap-1">
        <button onClick={onToggle} className="flex-1 text-left">
          <div className="flex items-center gap-1">
            {row.success ? (
              <Check size={10} className="shrink-0 text-green-500" />
            ) : (
              <X size={10} className="shrink-0 text-destructive" />
            )}
            <span className="truncate font-mono text-[11px] font-medium">{row.modelId}</span>
          </div>
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation()
            onDelete()
          }}
          disabled={deleteDisabled}
          className="shrink-0 text-muted-foreground hover:text-destructive disabled:opacity-40"
        >
          <Trash2 size={10} />
        </button>
      </div>
      <button onClick={onToggle} className="block w-full text-left">
        <div className="mb-1 flex flex-wrap items-center gap-1 text-[10px] text-muted-foreground">
          <span>{row.latencyMs}ms</span>
          {row.totalCostUsd != null && row.totalCostUsd > 0 && <span>${row.totalCostUsd.toFixed(6)}</span>}
          {row.totalTokens != null && <span>{row.totalTokens}t</span>}
        </div>
        <div
          className={cn(
            "rounded bg-secondary/50 p-1.5 font-mono text-[11px] text-foreground",
            !expanded && "line-clamp-3",
          )}
        >
          {row.success ? (row.responseText ?? "(empty)") : (row.errorMessage ?? "(failed)")}
        </div>
        {expanded && (
          <div className="mt-2 space-y-1.5">
            <div className="rounded border border-border/50 p-1.5">
              <div className="mb-0.5 text-[9px] uppercase tracking-wide text-muted-foreground">System</div>
              <div className="whitespace-pre-wrap text-[11px]">{row.systemPrompt}</div>
            </div>
            <div className="rounded border border-border/50 p-1.5">
              <div className="mb-0.5 text-[9px] uppercase tracking-wide text-muted-foreground">User</div>
              <div className="whitespace-pre-wrap text-[11px]">{row.userPrompt}</div>
            </div>
            <div className="flex flex-wrap gap-1">
              {(row.tags ?? []).map((t) => (
                <Badge key={t} variant="secondary" className="text-[9px]">
                  {t}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </button>
    </div>
  )
}
